import { Editor as EditorField } from "@tinymce/tinymce-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { flushSync } from "react-dom";
import {
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { type SxProps } from "@mui/material/styles";

import * as cheerio from "cheerio";
import { concat, groupBy, kebabCase, orderBy, slice } from "lodash";
import { Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setContent as setConfirmDialogContent,
  setOnChange as setConfirmDialogOnChange,
  setOpen as setConfirmDialogOpen,
  setOptions as setConfirmDialogOptions,
  setTitle as setConfirmDialogTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  FORM_PAGE_DPI,
  FormSettings,
  formContentWidthPx
} from "components/pdf-generation-v3/form-print.utils";
import SmsSegmentCounter from "components/sms-segment-counter/sms-segment-counter.component";
import {
  AI_PROMPT_INTERFACE_FEATURE,
  ANVIL_MERGE_TAG_PARITY,
  CLINICAL_MERGE_TAGS,
  CURRENT_APPOINTMENT_MERGE_TAG,
  DEEPGRAM_SPEECH_TO_TEXT,
  DIGITAL_FORM_TAGS_PURCHASE_AGREEMENT,
  HTML_FORMS_FEATURE,
  INVOICE_TABLE_TAG_EDIT_FEATURE,
  PATIENT_PRONOUNS_FEATURE
} from "constants/feature-flags";
import { selectCurrentUserToken } from "features/auth/auth-slice";
import { selectMedicalScribeDeviceSettings } from "features/medical-scribe/medical-scribe.slice";
import { normalizeMicrophoneDeviceId } from "features/medical-scribe/microphone.utils";
import {
  LAST_FOCUSED_WORD_PROCESSOR_FIELD,
  eventBus
} from "hooks/use-event-bus/event-bus";
import useLatestRef from "hooks/use-latest-ref.hook";
import {
  useGetCommsTextTemplatesQuery,
  useLazyGetCommsTextTemplateQuery
} from "services/suno-comms/suno-comms-api";
import type { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import {
  useGetTextTemplatesQuery,
  useLazyGetTextTemplateQuery
} from "services/text-templates";
import type { Appointment } from "services/types/appointments.type";
import type {
  TextTemplate,
  TextTemplateInsertedPayload,
  TextTemplateListItem
} from "services/types/text-templates.type";
import { convertHtmlToText, sanitizeHTML } from "utils/common";
import { getTextTemplateSubjectValue } from "utils/message-templates.util";
import {
  calculateTemplateSmsSegments,
  getSmsTemplateOverLimitMessage,
  shouldEstimateSmsMergeTags
} from "utils/sms-template-length.util";
import { viteEnv } from "utils/vite-env.util";

import AiPromptInlineField from "./ai-prompt/ai-prompt-inline-field.component";
import { addBlankFields } from "./blank-fields.utils";
import { addCheckboxToggle } from "./checkbox-glyph-toggle.utils";
import MergeTagsLoadingBar from "./components/merge-tags-loading-bar.component";
import {
  type InvoiceTableTagRequest,
  sameTagOptions,
  withUnknownOptions
} from "./dialogs/invoice-table-tag-options.utils";
import WordProcessorSelectSaleConfigurationDialog from "./dialogs/word-processor-select-sale-configuration-dialog.component";
import WordProcessorSelectSaleDialog from "./dialogs/word-processor-select-sale-dialog.component";
import WordProcessorSilenceWarningDialog from "./dialogs/word-processor-silence-warning-dialog.component";
import { createFormImageUpload } from "./form-image.utils";
import {
  FORM_EDITOR_TOOLBAR,
  addFormEditorButtons,
  buildFormModeEditorConfig,
  chipTagsWithOptions
} from "./form-mode-editor-config.utils";
import {
  addTableLinesButton,
  withTableLinesButton
} from "./form-table-lines.utils";
import { useWordProcessorId } from "./hooks/use-word-processor-id.hook";
import {
  type InvoiceTableTagTarget,
  attachInvoiceTableTagEditing,
  invoiceTableTag,
  invoiceTableTagOptions,
  isInvoiceTableTag,
  placeCaretAfterInvoiceTableTag,
  removeInvoiceTableTag,
  replaceInvoiceTableTag
} from "./invoice-table-tag.utils";
import {
  PATIENT_SELECTED_INVOICE_OPTIONS,
  getPatientSelectedInvoiceMenuOptions
} from "./merge-tags/merge-tags-options/patient-selected-invoice.utils";
import {
  type MergeTagMenuEntry,
  stripSignatureMergeTags
} from "./merge-tags/merge-tags-options/signature-merge-tags.utils";
import {
  decodeItemsAsDrawnOptions,
  itemsAsDrawnTableHtml
} from "./merge-tags/merge-tags-values/common/items-as-drawn.util";
import {
  getSaleDeviceLineItems,
  getSaleLineItemsServicesTableHtml,
  getSaleLineItemsTableHtml,
  getSaleLineItemsTableHtmlWithPredefinedParameters,
  getSaleLineItemsTotalsTableHtml
} from "./merge-tags/merge-tags-values/common/sale-items-table.utils";
import {
  MERGE_TAGS_OPTIONS,
  MERGE_TAGS_OPTIONS_WITH_PRONOUNS,
  buildMergeTagsOptions,
  getPatientSignatureMergeTagOrPlaceholder,
  replaceMergeTagInContent
} from "./merge-tags/merge-tags.utils";
import useMergeTags from "./merge-tags/use-merge-tags.hook";
import SignDocumentDialog from "./sign-document";
import useSignDocument from "./sign-document/use-sign-document.hook";
import useSpeechToText from "./speech-to-text";
import useSpeechToTextStreaming from "./speech-to-text/use-speech-to-text-streaming.hook";
import TextTemplatesDialogV2 from "./text-templates/text-templates-v2";
import { mapPatientMergeTagsForLeads } from "./text-templates/text-templates-v2/cross-platform-merge-tags.util";
import {
  commsTemplateToTextTemplate,
  getTemplateSource
} from "./text-templates/text-templates-v2/text-templates-v2.utils";
import { getTextWithMergeTagsValues } from "./text-templates/text-templates.utils";
import { useTemplateSearch } from "./text-templates/use-template-search.hook";
import useTextTemplates, {
  type SubmitTemplateCallback
} from "./text-templates/use-text-templates.hook";
import WordProcessorErrorState from "./word-processor-error-state.component";
import {
  CONTACT_FORM_PLACEHOLDER_HTML,
  CONTACT_FORM_PLACEHOLDER_ID,
  filePickerCallback,
  imagesUploadHandler,
  removeDefaultShortcuts,
  resetSpeechToTextButton,
  setupWordProcessor
} from "./word-processor-field-setup.utils";
import {
  WordProcessorFeatureOptions,
  WordProcessorFeaturesGroup,
  getFeatureOptions,
  getPlugins,
  getToolbarFeatures,
  hideToolbar,
  showToolbar
} from "./word-processor-field.utils";
import WordProcessorLoadingSkeleton from "./word-processor-loading-skeleton";
import {
  isWordProcessorValueEmpty,
  resolveRequiredRule
} from "./word-processor-required.utils";
import { getParsedWordProcessorValue } from "./word-processor-value.utils";
import { getHtmlLikeText, getTextLength } from "./word-processor-value.utils";
import {
  clearAiPromptDetails,
  selectDataParamsById,
  setDataParams
} from "./word-processor.slice";

const apiKey = viteEnv.VITE_TINYMCE_API_KEY;

/** `getToolbarFeatures` returns `false` for a read-only editor, which must stay toolbar-less. */
const appendToolbarItems = (
  toolbar: string | false,
  extra: string | undefined
): string | false => {
  if (toolbar === false || !extra) return toolbar;
  return `${toolbar} | ${extra}`;
};

export interface FieldInnerStates {
  pageBreakSelected?: any;
}

type WordProcessorFieldProps = {
  name: string;
  required?: boolean;
  autoFocus?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  defaultFontSize?: string | null | number | undefined;
  featuresGroup?: WordProcessorFeaturesGroup;
  maxLength?: number;
  templateTypes?: number[];
  sx?: SxProps;
  aiPromptInlineFieldSx?: SxProps;
  onChange?: (content: string, text: string, element: HTMLElement) => void;
  onFieldValidation?: (name: string, isValid: boolean) => void;
  onSignDocument?: (formData: FormData) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onTemplateInserted?: (template: TextTemplate | null) => void;
  height?: number;
  mergeTagsPatientId?: number; //if not set current patient will be used
  mergeTagsExcludeChartNoteId?: number; //chart note to exclude from "my last chart note" lookup (e.g. the note being edited)
  mergeTagsSaleId?: number;
  // The appointment the document being edited is bound to (e.g. a chart note's
  // own appointment). Resolves {{Appointment.Provider.*}} tags off this
  // appointment's staff member, independent of the signed-in user (ENG-1644).
  mergeTagsAppointment?: Appointment;
  allowLeadsContext?: boolean;
  hidden?: boolean;
  mergeTagsOptions?: typeof MERGE_TAGS_OPTIONS;
  /**
   * Extra groups appended to whatever list is in effect. Unlike `mergeTagsOptions`, which REPLACES
   * the built-in list (and with it the invoice group and the flag-gated appointment/clinical tags),
   * this only adds — use it to offer a namespace on one surface without rebuilding the default.
   */
  extraMergeTagsOptions?: MergeTagMenuEntry[];
  preventLineBreak?: boolean;
  autoFetchMergeTagsValues?: boolean;
  afterInitCallback?: (editor: Editor) => void;
  afterMergeTagsDataLoadedCallback?: (callback: SubmitTemplateCallback) => void;
  onMergeTagsContextReady?: (ctx: {
    getMergeTagValue: (
      mergeTag: string,
      options?: Record<string, any>
    ) => string | null | undefined;
    isReady: boolean;
  }) => void;
  onClick?: () => void;
  askAiInputPlaceholder?: string;
  askAiPromptType?: number;
  onAskAi?: (params: {
    id: string;
    prompt: string;
    fieldContent: string;
    lastResult?: string;
  }) => Promise<void> | undefined;
  onIdGenerated?: (id: string) => void;
  /** ENG-2447: the editor once it is ready, for a sibling that reads its body (the Fill in the blanks panel). */
  onEditorInit?: (editor: Editor) => void;
  replaceMergeTagName?: (mergeTag: string) => string;
  enableHandSignatureOnly?: boolean;
  patientSignature?: File;
  additionalMergeTagsValues?: Record<string, any>;
  pasteAsTextByDefault?: boolean;
  maxImageSizeMB?: number;
  hideLengthLimitation?: boolean;
  /**
   * SMS mode for the length feedback (text-only fields): replaces the plain
   * `n / maxLength` counter with an encoding-aware character + segment counter
   * (GSM-7 vs UCS-2) and replaces the `maxLength` validation with the true
   * carrier deliverability ceiling (1530 GSM / 670 UCS-2 characters). The
   * counter reads the RHF value, so programmatic set values (prefill, AI
   * autocomplete) stay in sync.
   *
   * On a template surface — merge tags offered but their values not insertable —
   * each `{{merge.tag}}` is scored at its estimated resolved length instead of
   * its token length, and the counter says so. Nothing to pass for that; it is
   * inferred from the field's own merge-tag options.
   */
  smsSegmentCounter?: boolean;
  /**
   * Overrides that inference. Only needed by a field that inserts merge-tag
   * VALUES inline — so it looks like a plain message — but whose text is still
   * run through a server-side template renderer before it is sent. The
   * touchpoint fields are the case: the picker resolves against the known
   * patient, yet `make_message` in the backend's `marketing/workflows/
   * workflow.py` calls `format_template(touchpoint.message)`, so a literal tag
   * that arrived by hand (or from a template inserted before a patient was
   * picked) still expands at send time and must be scored as such.
   */
  estimateSmsMergeTags?: boolean;
  /** Stacking order for Templates (and related) dialogs opened from this field. */
  nestedDialogZIndex?: number;
  /** Backend the template picker reads from. Omitted ⇒ "default" (main backend). */
  templateSource?: TemplateSourceType;
  /**
   * Resolves the contact-form placeholder HTML to insert (e.g. from a settings dialog owned by the
   * consumer), or `null` to cancel. When omitted, a default placeholder is inserted. MUST be
   * referentially stable — it feeds the editor `init` and an unstable value re-initializes TinyMCE.
   */
  onRequestContactFormPlaceholder?: () => Promise<string | null>;
  /**
   * Extra editor setup (e.g. a surface's own autocompleter or toolbar button), run inside TinyMCE's
   * `setup` after the shared setup. Autocompleters MUST be registered here, not after init: TinyMCE
   * caches its autocompleter registry on the first keystroke. Read ONCE, when the editor first
   * initialises (tinymce-react ignores later `init` changes), so a value that appears after mount
   * has no effect until the editor remounts. Keep it referentially stable anyway: it is a dependency
   * of the memoised `init`.
   */
  onEditorSetup?: (editor: Editor) => void;
  /** Toolbar button names (registered in `onEditorSetup`) appended to the toolbar. Must be stable. */
  extraToolbarItems?: string;
  /** CSS appended to the editor iframe's `content_style`. Must be stable. */
  extraContentStyle?: string;
  /**
   * ENG-2277 (HTML Forms): a Form's print settings. When set, the editor shows the Form on its own
   * page in the fonts it prints in, new tables take its default lines and paste keeps its layout.
   * Absent (or null), the editor is configured exactly as before.
   */
  formSettings?: FormSettings | null;
  /**
   * ENG-2277: the Digital Form editor's toolbar — always shown, wrapping instead of folding, with
   * labelled Insert field, Signature line and Edit HTML buttons whose pickers the page owns. Only
   * read with `formSettings`; absent, the toolbar is the usual one.
   */
  formEditorToolbar?: FormEditorToolbarActions;
} & WordProcessorFeatureOptions;

export type FormEditorToolbarActions = {
  onInsertField: () => void;
  onSignatureLine: () => void;
};

export type RhfWordProcessorFieldProps<T extends FieldValues> =
  WordProcessorFieldProps & UseControllerProps<T>;

// The running editor's Form page style, replaced when the Form's settings change.
const FORM_MODE_LIVE_STYLE_ID = "suno-form-page";

export default function WordProcessorField<T extends FieldValues>({
  label,
  name,
  autoFocus,
  isLoading,
  loadingText,
  required,
  placeholder,
  featuresGroup = "default",
  helperText,
  defaultFontSize,
  control,
  maxLength,
  templateTypes,
  sx,
  aiPromptInlineFieldSx,
  rules,
  onChange: afterChange,
  onFieldValidation,
  onSignDocument,
  onKeyDown,
  onFocus,
  onBlur,
  onTemplateInserted,
  height,
  mergeTagsPatientId,
  mergeTagsExcludeChartNoteId,
  mergeTagsSaleId,
  mergeTagsAppointment,
  allowLeadsContext,
  hidden,
  readOnly,
  allowUndoRedo,
  allowHtml,
  allowLists,
  allowAlignments,
  allowTables,
  allowImages,
  allowTemplates,
  allowSignDocument,
  allowMergeTags,
  allowInsertMergeTagsValues,
  allowPasteAsText,
  allowCode,
  allowRemoveFormat,
  allowFullscreen,
  allowPreview,
  allowSpeechToText,
  allowToolbarAutohide,
  allowShowLength,
  allowSelectInvoice,
  allowInvoiceMergeTags,
  allowAIPromptInterface,
  allowImageAutoUpload,
  allowContactFormPlaceholder,
  mergeTagsOptions: initialMergeTagsOptions,
  extraMergeTagsOptions,
  onEditorSetup,
  extraToolbarItems,
  extraContentStyle,
  preventLineBreak = false,
  autoFetchMergeTagsValues = false,
  afterInitCallback,
  afterMergeTagsDataLoadedCallback,
  onMergeTagsContextReady,
  onClick,
  onAskAi,
  askAiInputPlaceholder,
  askAiPromptType,
  onIdGenerated,
  onEditorInit,
  replaceMergeTagName,
  enableHandSignatureOnly = true,
  patientSignature,
  additionalMergeTagsValues,
  pasteAsTextByDefault,
  maxImageSizeMB,
  hideLengthLimitation,
  smsSegmentCounter,
  estimateSmsMergeTags: estimateSmsMergeTagsProp,
  nestedDialogZIndex,
  templateSource,
  onRequestContactFormPlaceholder,
  formSettings,
  formEditorToolbar
}: RhfWordProcessorFieldProps<T>) {
  const dispatch = useAppDispatch();
  const flags = useFlags();

  const isAIPromptInterfaceEnabled = flags[AI_PROMPT_INTERFACE_FEATURE];
  const isDeepGramSpeechToTextEnabled = flags[DEEPGRAM_SPEECH_TO_TEXT];
  const isCurrentAppointmentMergeTagEnabled =
    flags[CURRENT_APPOINTMENT_MERGE_TAG] ?? false;
  const pronounsFeatureEnabled = flags[PATIENT_PRONOUNS_FEATURE];
  const baseMergeTagsOptions = pronounsFeatureEnabled
    ? MERGE_TAGS_OPTIONS_WITH_PRONOUNS
    : MERGE_TAGS_OPTIONS;
  const isClinicalMergeTagsEnabled = flags[CLINICAL_MERGE_TAGS] ?? false;
  const isAnvilParityEnabled = flags[ANVIL_MERGE_TAG_PARITY] ?? false;
  const isPurchaseAgreementTagsEnabled = Boolean(
    flags[DIGITAL_FORM_TAGS_PURCHASE_AGREEMENT]
  );

  const {
    readOnly: readOnlyFeature,
    allowUndoRedo: allowUndoRedoFeature,
    allowHtml: allowHtmlFeature,
    allowLists: allowListsFeature,
    allowAlignments: allowAlignmentsFeature,
    allowTables: allowTablesFeature,
    allowImages: allowImagesFeature,
    allowTemplates: allowTemplatesFeature,
    allowSignDocument: allowSignDocumentFeature,
    allowMergeTags: allowMergeTagsFeature,
    allowInsertMergeTagsValues: allowInsertMergeTagsValuesFeature,
    allowPasteAsText: allowPasteAsTextFeature,
    allowCode: allowCodeFeature,
    allowRemoveFormat: allowRemoveFormatFeature,
    allowFullscreen: allowFullscreenFeature,
    allowPreview: allowPreviewFeature,
    allowSpeechToText: allowSpeechToTextFeature,
    allowToolbarAutohide: allowToolbarAutohideFeature,
    allowShowLength: allowShowLengthFeature,
    allowSelectInvoice: allowSelectInvoiceFeature,
    allowInvoiceMergeTags: allowInvoiceMergeTagsFeature,
    allowAIPromptInterface: allowAIPromptInterfaceFeature,
    allowImageAutoUpload: allowImageAutoUploadFeature,
    allowContactFormPlaceholder: allowContactFormPlaceholderFeature,
    optionsUpdated
  } = useMemo(
    () =>
      getFeatureOptions(featuresGroup, {
        readOnly,
        allowUndoRedo,
        allowHtml,
        allowLists,
        allowAlignments,
        allowTables,
        allowImages,
        allowTemplates,
        allowSignDocument,
        allowMergeTags,
        allowInsertMergeTagsValues,
        allowPasteAsText,
        allowCode,
        allowRemoveFormat,
        allowFullscreen,
        allowPreview,
        allowSpeechToText,
        allowToolbarAutohide,
        allowShowLength,
        allowSelectInvoice,
        allowInvoiceMergeTags,
        allowAIPromptInterface: allowAIPromptInterface,
        allowImageAutoUpload,
        allowContactFormPlaceholder
      }),
    [
      featuresGroup,
      readOnly,
      allowUndoRedo,
      allowHtml,
      allowLists,
      allowAlignments,
      allowTables,
      allowImages,
      allowTemplates,
      allowSignDocument,
      allowMergeTags,
      allowInsertMergeTagsValues,
      allowPasteAsText,
      allowCode,
      allowRemoveFormat,
      allowFullscreen,
      allowPreview,
      allowSpeechToText,
      allowToolbarAutohide,
      allowShowLength,
      allowSelectInvoice,
      allowInvoiceMergeTags,
      allowAIPromptInterface,
      allowImageAutoUpload,
      allowContactFormPlaceholder
    ]
  );

  const token = useAppSelector(selectCurrentUserToken);
  // Speech-to-text shares the microphone the user picked (and verified via the
  // Suno Scribe mic test) instead of silently falling back to the OS default input.
  const { microphone: storedMicrophoneDeviceId } = useAppSelector(
    selectMedicalScribeDeviceSettings
  );
  // "default"/"communications" are OS-level proxies, not real device ids — they are
  // what the slice stores when the user never picked a specific mic, and they are
  // not resolvable device ids on Firefox. Send no constraint for those, which is
  // what this field did before it read the setting at all.
  const selectedMicrophoneDeviceId = useMemo(
    () => normalizeMicrophoneDeviceId(storedMicrophoneDeviceId),
    [storedMicrophoneDeviceId]
  );

  const fieldIdRef = useRef<string | undefined>();
  const fieldInnerStatesRef = useRef<FieldInnerStates>({});
  const patientSignatureURLRef = useRef<string | undefined>();

  const templateQueryParams = useMemo(
    () => ({
      query: "{id,name,type,category}",
      ...(templateTypes?.length ? { type: templateTypes } : {})
    }),
    [templateTypes]
  );

  const isLeads = templateSource === "leads";

  const commsTemplateQueryParams = useMemo(
    () => ({
      ...(templateTypes?.length ? { type: templateTypes } : {}),
      limit: 300,
      ordering: "name"
    }),
    [templateTypes]
  );

  // Main-app templates are always read; Leads surfaces additionally read the
  // native Comms templates and browse both stores merged (SN-18785).
  const { data: defaultTextTemplates } = useGetTextTemplatesQuery(
    templateQueryParams,
    { skip: !allowTemplatesFeature }
  );
  const { data: commsTextTemplates } = useGetCommsTextTemplatesQuery(
    commsTemplateQueryParams,
    { skip: !isLeads || !allowTemplatesFeature }
  );

  // Memoize the Comms → TextTemplate mapping. A fresh array every render would
  // churn useWordProcessorId's textTemplates dep and dispatch setDataParams in a
  // loop (Maximum update depth exceeded).
  const allTextTemplates = useMemo(() => {
    if (isLeads) {
      return [
        ...(commsTextTemplates ?? []).map(commsTemplateToTextTemplate),
        ...(defaultTextTemplates ?? [])
      ];
    }
    return defaultTextTemplates;
  }, [isLeads, commsTextTemplates, defaultTextTemplates]);

  const textTemplates = useMemo(() => {
    if (!allowTemplatesFeature) return [];
    const filteredTemplates = allTextTemplates || [];

    // group templates by category.id
    const groupedTemplates = groupBy(
      filteredTemplates,
      (template) => template.category?.id || -1
    );

    return Object.values(groupedTemplates).flatMap((templates) =>
      orderBy(templates, [(template) => template.category?.title || "", "name"])
    );
  }, [allTextTemplates, allowTemplatesFeature]);

  const {
    isReady: isMergeTagsDataSourceReady,
    setDoNotFetchMergeTagsDataSources,
    getMergeTagValue,
    getMergeTagNameOrEmptyValue,
    contextSale,
    isContextSaleLoading
  } = useMergeTags({
    fieldIdRef,
    allowMergeTags: allowMergeTagsFeature,
    allowInsertMergeTagsValues: allowInsertMergeTagsValuesFeature,
    allowHtml: allowHtmlFeature,
    maxLength,
    patientId: mergeTagsPatientId,
    excludeChartNoteId: mergeTagsExcludeChartNoteId,
    patientSignatureURLRef,
    additionalMergeTagsValues,
    allowLeadsContext,
    boundAppointment: mergeTagsAppointment
  });

  const mergeTagsOptions = useMemo(() => {
    const withExtras = (options: MergeTagMenuEntry[]): MergeTagMenuEntry[] =>
      extraMergeTagsOptions?.length
        ? concat(options, extraMergeTagsOptions)
        : options;

    // SN-19323: a signature tag resolves to an <img>, which a text-only field
    // strips to nothing — the insert silently cancels. Don't offer tags that
    // can only fail. Template-authoring fields (values off) keep them: there
    // the literal token persists for HTML surfaces to resolve later.
    const stripUnsupportedTags = <TGroup extends MergeTagMenuEntry>(
      options: TGroup[]
    ): TGroup[] =>
      allowInsertMergeTagsValuesFeature && !allowHtmlFeature
        ? options.map((group) => stripSignatureMergeTags(group))
        : options;

    const prepareOptions = (options: MergeTagMenuEntry[]) =>
      stripUnsupportedTags(withExtras(options));

    if (initialMergeTagsOptions) {
      return prepareOptions(initialMergeTagsOptions);
    }

    const baseOptions = buildMergeTagsOptions(
      isCurrentAppointmentMergeTagEnabled,
      isClinicalMergeTagsEnabled,
      baseMergeTagsOptions,
      isAnvilParityEnabled
    );

    if (allowInvoiceMergeTagsFeature) {
      // The invoice group goes after the last signed report, one place later when the parity flag
      // adds the Medicare group ahead of it (SN-19877).
      const invoiceIndex = isAnvilParityEnabled ? 6 : 5;
      const result = concat(
        slice(baseOptions, 0, invoiceIndex),
        getPatientSelectedInvoiceMenuOptions({
          allowInvoiceMergeTagsFeature: allowInvoiceMergeTagsFeature,
          allowSelectInvoice:
            allowInsertMergeTagsValuesFeature && allowSelectInvoiceFeature,
          sale: contextSale,
          purchaseAgreementTags: isPurchaseAgreementTagsEnabled
        }),
        slice(baseOptions, invoiceIndex)
      );
      return prepareOptions(result);
    }
    return prepareOptions(baseOptions);
  }, [
    initialMergeTagsOptions,
    extraMergeTagsOptions,
    allowInsertMergeTagsValuesFeature,
    allowHtmlFeature,
    allowSelectInvoiceFeature,
    contextSale,
    allowInvoiceMergeTagsFeature,
    isCurrentAppointmentMergeTagEnabled,
    isClinicalMergeTagsEnabled,
    baseMergeTagsOptions,
    isAnvilParityEnabled,
    isPurchaseAgreementTagsEnabled
  ]);

  const mergeTagsValuesEnabled =
    allowMergeTagsFeature && allowInsertMergeTagsValuesFeature && !readOnly;

  /**
   * A field that offers merge tags but cannot insert their values stores the
   * literal token and leaves resolution to whatever sends the message — so its
   * SMS length has to be projected, not measured. Same condition `getTextLength`
   * uses to decide whether to subtract tags, which is why no new prop is needed.
   */
  const estimateSmsMergeTags = shouldEstimateSmsMergeTags({
    explicit: estimateSmsMergeTagsProp,
    allowMergeTags: allowMergeTagsFeature,
    allowInsertMergeTagsValues: allowInsertMergeTagsValuesFeature
  });

  const editorRef = useRef<Editor | null>(null);
  const [initialized, setInitialized] = useState<number>(0);
  const [hasLoadError, setHasLoadError] = useState<boolean>(false);
  const [isInitialContentLoading, setIsInitialContentLoading] =
    useState<boolean>(true);

  const [isRecording, setIsRecording] = useState(false);

  const [speechToTextChunk, setSpeechToTextChunk] = useState<
    string | undefined
  >(undefined);

  const dataParams = useSelector((state: any) =>
    selectDataParamsById(state, String(fieldIdRef.current || ""))
  );

  const saleOptionsRef = useRef<Record<string, any> | undefined>(undefined);

  useEffect(() => {
    saleOptionsRef.current = dataParams?.saleConfiguration;
  }, [dataParams]);

  const id = useWordProcessorId({
    fieldIdRef,
    name,
    featuresGroup,
    optionsUpdated,
    mergeTagsPatientId,
    mergeTagsSaleId,
    defaultFontSize,
    contextSaleId: contextSale?.id,
    textTemplates,
    onIdGenerated,
    allowImageAutoUploadFeature
  });

  useEffect(() => {
    return () => {
      dispatch(clearAiPromptDetails(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ENG-1944: a caller may declare the field required either via the `required`
  // prop or via RHF `rules={{ required }}`. Both must arm the emptiness check —
  // the prop used to be the only source, and `controlRules` below overwrote the
  // caller's rule, so `rules={{ required: "..." }}` alone validated nothing.
  const {
    isRequired,
    message: requiredMessage,
    controlRequired
  } = useMemo(
    () => resolveRequiredRule({ required, rules, label }),
    [required, rules, label]
  );

  const validate = useCallback(
    (value: any) => {
      if (readOnlyFeature) return true;

      if (!allowHtmlFeature && smsSegmentCounter) {
        const info = calculateTemplateSmsSegments(
          convertHtmlToText(String(value)),
          estimateSmsMergeTags
        );
        if (info.isOverLimit) {
          return getSmsTemplateOverLimitMessage(info);
        }
      } else if (
        !allowHtmlFeature &&
        maxLength &&
        getTextLength(
          convertHtmlToText(String(value)),
          Boolean(!allowInsertMergeTagsValuesFeature)
        ) > maxLength
      ) {
        return `${
          label || "Field"
        } must be no more than ${maxLength} characters long`;
      }

      // HTML-empty values (`<p></p>`, `<p><br></p>`, `&nbsp;`) pass RHF's
      // built-in `required` check, so the text projection is what decides.
      if (isRequired && isWordProcessorValueEmpty(value)) {
        return requiredMessage;
      }

      return true;
    },
    [
      allowHtmlFeature,
      allowInsertMergeTagsValuesFeature,
      estimateSmsMergeTags,
      isRequired,
      label,
      maxLength,
      readOnlyFeature,
      requiredMessage,
      smsSegmentCounter
    ]
  );

  const controlRules = useMemo(
    () => ({
      ...rules,
      // The caller's rule wins; the prop is the fallback. Never overwrite a
      // caller's `required` with `{ value: false }`.
      required: controlRequired,
      validate
    }),
    [controlRequired, rules, validate]
  );

  const {
    field: { onChange, value, ...field },
    fieldState: { error, invalid }
  } = useController<T>({
    name,
    control,
    rules: controlRules
  });

  const [isBusy, setIsBusy] = useState<boolean>();
  useEffect(() => {
    setIsBusy(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!allowHtmlFeature) {
      onChange(getHtmlLikeText(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowHtmlFeature, onChange, id]);

  // Note: Avoid toggling isInitialContentLoading on value changes to prevent
  // blocking onEditorChange from propagating user edits.

  const isValid = !Boolean(invalid);
  const errorMessage = error?.message;
  const [length, setLength] = useState(0);

  // Derived from the RHF value (not editor events) so prefills and other
  // programmatic setValue calls can never leave the counter stale.
  const smsCounterText = useMemo(
    () => (smsSegmentCounter ? convertHtmlToText(String(value ?? "")) : ""),
    [smsSegmentCounter, value]
  );

  useEffect(() => {
    if (
      initialized &&
      (maxLength || allowShowLengthFeature) &&
      value &&
      editorRef.current
    ) {
      let text = editorRef.current.getContent({ format: "text" });

      const textLength = getTextLength(
        text,
        Boolean(!allowInsertMergeTagsValuesFeature)
      );

      setLength(textLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    maxLength,
    allowHtmlFeature,
    allowInsertMergeTagsValuesFeature,
    allowShowLengthFeature
  ]);

  useEffect(() => {
    if (onFieldValidation) {
      onFieldValidation(name, isValid);
    }
  }, [isValid, onFieldValidation, name]);

  const handleChangeValue = useCallback(
    (html: string | undefined = "", editor?: Editor) => {
      onChange(html);

      if (afterChange && editor) {
        afterChange(
          html,
          editor.getContent({ format: "text" }),
          editor.getBody()
        );
      }
    },
    [onChange, afterChange]
  );

  // Landing Pages only: insert the contact-form placeholder, or warn if one
  // already exists. Deps are all referentially stable (ref, dispatch, stable
  // callback) so `setupHandler`/`init` identity is unchanged — no editor re-init.
  const handleInsertContactFormPlaceholder = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return;

    const alreadyAdded =
      editor.dom.select(`[data-id="${CONTACT_FORM_PLACEHOLDER_ID}"]`).length >
      0;
    if (alreadyAdded) {
      dispatch(setConfirmDialogTitle("Contact form placeholder"));
      dispatch(
        setConfirmDialogContent("A contact form placeholder is already added.")
      );
      dispatch(
        setConfirmDialogOptions({
          actionButtonText: "OK",
          hideCancelButton: true
        })
      );
      dispatch(setConfirmDialogOnChange(() => undefined));
      dispatch(setConfirmDialogOpen(true));
      return;
    }

    const html = onRequestContactFormPlaceholder
      ? await onRequestContactFormPlaceholder()
      : CONTACT_FORM_PLACEHOLDER_HTML;
    if (!html) return;

    editor.focus();
    editor.insertContent(html);
    handleChangeValue(editor.getContent(), editor);
  }, [dispatch, handleChangeValue, onRequestContactFormPlaceholder]);

  const onEditorChangeHandler = useCallback(
    (html: string, editor: Editor) => {
      if (speechToTextChunk || isInitialContentLoading) return;

      if (maxLength || allowShowLengthFeature) {
        let text = editor.getContent({ format: "text" });

        const textLength = getTextLength(
          text,
          Boolean(!allowInsertMergeTagsValuesFeature)
        );
        setLength(textLength);
      }
      handleChangeValue(html, editor);
    },
    [
      allowInsertMergeTagsValuesFeature,
      handleChangeValue,
      maxLength,
      speechToTextChunk,
      allowShowLengthFeature,
      isInitialContentLoading
    ]
  );

  // Read through a ref: keyed on `value`, the callback below changed with every edit and upload, and
  // the effect that calls it ran again each time, writing the signature in and setting the content
  // again without end (ENG-2421: 425 SetContents in ten seconds after signing).
  const valueRef = useLatestRef(value);
  const handleReplacePlaceholderToSignature = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        patientSignatureURLRef.current = base64String;

        const $ = cheerio.load(valueRef.current);
        $("button.sign-button, img.patient-signature").replaceWith(
          getPatientSignatureMergeTagOrPlaceholder(base64String)
        );
        onChange($.html());
      };

      reader.onerror = (error) => {
        console.error("Error reading signature file:", error);
      };

      reader.onabort = () => {
        console.warn("Signature file reading was aborted");
      };

      try {
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error starting file read operation:", error);
      }
    },
    [onChange, valueRef]
  );

  const onSignDocumentCallback = useCallback(
    async (file?: File | null) => {
      const formData = new FormData();
      if (file) {
        formData.append("signature", file);

        handleReplacePlaceholderToSignature(file);
      } else {
        formData.append("signature", "");
      }

      if (onSignDocument) {
        await onSignDocument(formData);
      }
    },
    [handleReplacePlaceholderToSignature, onSignDocument]
  );

  const {
    showSignDialog,
    handleSubmitSignature,
    handleShowSignDocumentDialog,
    handleCloseSignDocumentDialog
  } = useSignDocument({ onSignDocumentCallback });

  const onAppendSpeechToText = useCallback(
    (text: string | undefined = "") => {
      if (!editorRef.current || text.trim() === "") return;

      setSpeechToTextChunk(text);

      editorRef.current?.insertContent(text);
      handleChangeValue(editorRef.current.getContent(), editorRef.current);

      setSpeechToTextChunk(undefined);
    },
    [handleChangeValue]
  );

  // Speech-to-text could not start (no mic, permission denied, socket/auth
  // failure). Drop out of the recording state so the toolbar mic does not stay
  // lit while nothing is being transcribed, and tell the user why.
  const handleSpeechToTextStartFailed = useCallback(() => {
    setIsRecording(false);
    if (editorRef.current) {
      resetSpeechToTextButton(editorRef.current);
    }
    dispatch(
      setCustomMessage(
        "Could not start speech to text. Check that your microphone is connected and that the browser has permission to use it."
      )
    );
    dispatch(setOpen(true));
  }, [dispatch]);

  const { sendContinueRecording } = useSpeechToTextStreaming({
    isRecording,
    onAppendText: onAppendSpeechToText,
    enabled: isDeepGramSpeechToTextEnabled,
    onPauseDueToSilence: useCallback(() => {
      setIsRecording(false);
      if (editorRef.current) {
        resetSpeechToTextButton(editorRef.current);
      }
    }, []),
    onSilenceDetected: useCallback(() => {
      dispatch(
        setDataParams({
          id: fieldIdRef.current,
          showSilenceDialog: true
        })
      );
    }, [dispatch]),
    microphoneDeviceId: selectedMicrophoneDeviceId,
    onStartFailed: handleSpeechToTextStartFailed
  });
  useSpeechToText({
    isRecording,
    onAppendText: onAppendSpeechToText,
    enabled: !isDeepGramSpeechToTextEnabled,
    microphoneDeviceId: selectedMicrophoneDeviceId,
    onStartFailed: handleSpeechToTextStartFailed
  });

  useEffect(() => {
    handleReplacePlaceholderToSignature(patientSignature);
  }, [handleReplacePlaceholderToSignature, patientSignature]);

  useEffect(() => {
    if (contextSale) {
      const $ = cheerio.load(value);

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.SALE_ITEMS,
        () =>
          getSaleLineItemsTableHtml(contextSale, getSaleDeviceLineItems, true)
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES,
        () => getSaleLineItemsTableHtml(contextSale, getSaleDeviceLineItems)
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES_NO_PRICE,
        () =>
          getSaleLineItemsTableHtml(
            contextSale,
            getSaleDeviceLineItems,
            false,
            true
          )
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES,
        () => getSaleLineItemsServicesTableHtml(contextSale)
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES_NO_PRICE,
        () => getSaleLineItemsServicesTableHtml(contextSale, true)
      );

      replaceMergeTagInContent($, PATIENT_SELECTED_INVOICE_OPTIONS.TOTALS, () =>
        getSaleLineItemsTotalsTableHtml(contextSale)
      );

      // ENG-2463: a converted purchase agreement's items table as the PDF draws it.
      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.ITEMS_AS_DRAWN,
        (options?: Record<string, any>) => {
          const layout = decodeItemsAsDrawnOptions(options);
          return layout ? itemsAsDrawnTableHtml(contextSale, layout) : "";
        }
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS,
        (options?: Record<string, any>) => {
          return getSaleLineItemsTableHtmlWithPredefinedParameters(
            contextSale,
            options ? options : (saleOptionsRef.current ?? {})
          );
        }
      );

      onChange($.html());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextSale]);

  const handleOnClickMergeTagSignButton = useCallback(() => {
    handleShowSignDocumentDialog();
  }, [handleShowSignDocumentDialog]);

  const handleOnClickSelectInvoiceButton = useCallback(() => {
    dispatch(
      setDataParams({
        id,
        openSelectSaleDialog: true
      })
    );
  }, [dispatch, id]);

  const handleSilenceWarningContinue = useCallback(() => {
    sendContinueRecording();
    dispatch(
      setDataParams({
        id,
        showSilenceDialog: false
      })
    );
  }, [dispatch, id, sendContinueRecording]);

  const handleSilenceWarningPause = useCallback(() => {
    setIsRecording(false);
    if (editorRef.current) {
      resetSpeechToTextButton(editorRef.current);
    }
  }, []);

  const handleSilenceWarningClose = useCallback(() => {
    dispatch(
      setDataParams({
        id,
        showSilenceDialog: false
      })
    );
  }, [dispatch, id]);

  const getInsertedTemplateSubject = useCallback(
    (subjectTemplate?: string | null) => {
      const subject = getTextTemplateSubjectValue(subjectTemplate);
      if (!subject || !mergeTagsValuesEnabled) return subject;

      return getTextTemplateSubjectValue(
        getTextWithMergeTagsValues({
          text: subject,
          getMergeTagValue,
          replaceMergeTagName,
          insertMergeTagValues: true,
          allowHtml: allowHtmlFeature
        })
      );
    },
    [
      allowHtmlFeature,
      getMergeTagValue,
      mergeTagsValuesEnabled,
      replaceMergeTagName
    ]
  );

  const {
    showTemplatesDialog,
    handleShowTemplatesDialog,
    handleCloseTemplatesDialog,
    handleSubmitTemplatesDialog
  } = useTextTemplates({
    handleChangeValue,
    editorRef,
    getMergeTagValue: mergeTagsValuesEnabled
      ? getMergeTagValue
      : getMergeTagNameOrEmptyValue,
    allowHtml: allowHtmlFeature,
    maxLength,
    replaceMergeTagName,
    insertMergeTagValues: mergeTagsValuesEnabled,
    getTemplateSubjectValue: getInsertedTemplateSubject
  });

  const fileCallback = useCallback(
    (cb: any) => {
      filePickerCallback(editorRef, cb, maxImageSizeMB);
    },
    [editorRef, maxImageSizeMB]
  );

  const imagesUploadCallback = useCallback(
    async (blobInfo: any, progress: (percent: number) => void) => {
      return imagesUploadHandler({
        token,
        autoUpload: Boolean(allowImageAutoUploadFeature),
        blobInfo,
        progress,
        maxImageSizeMB
      });
    },
    [maxImageSizeMB, token, allowImageAutoUploadFeature]
  );

  const [fetchTemplateText] = useLazyGetTextTemplateQuery();
  const [fetchCommsTemplateText] = useLazyGetCommsTextTemplateQuery();

  const prefetchTemplateText = useCallback(
    (templateId: number | string) => {
      // Only main-app rows need prefetching — they project text out. Comms
      // (Leads) rows carry full text inline, and their UUID ids fail the numeric
      // check below, so a merged list naturally prefetches only the main-app half.
      const numericId = Number(templateId);
      if (!Number.isNaN(numericId)) {
        void fetchTemplateText({
          id: numericId,
          query: "{text,subject_template}"
        });
      }
    },
    [fetchTemplateText]
  );
  const prefetchTemplateTextRef = useRef(prefetchTemplateText);
  prefetchTemplateTextRef.current = prefetchTemplateText;
  const stablePrefetchTemplateText = useCallback(
    (templateId: number | string) => {
      prefetchTemplateTextRef.current(templateId);
    },
    []
  );

  // Debounced server-side search for the inline "/" template autocompleter. The
  // pre-loaded list is capped at the backend's 300-row limit, so typing in the
  // dropdown must hit the backend to find templates beyond that cap (ENG-1426).
  const searchTemplates = useTemplateSearch(templateTypes, templateSource);

  const handleDropdownInsertTemplate = useCallback(
    async (template: TextTemplateListItem, editorInstance: Editor) => {
      if (!template?.id) {
        return;
      }

      const bookmark = editorInstance.selection.getBookmark(2);

      let text = "";
      let subjectTemplate: string | undefined;
      try {
        if (getTemplateSource(template) === "leads") {
          const inlineText = (template as TextTemplate).text;
          if (inlineText?.trim()) {
            text = inlineText;
            subjectTemplate = template.subject_template ?? undefined;
          } else {
            const result = await fetchCommsTemplateText(
              String(template.id)
            ).unwrap();
            text = result?.text ?? "";
            subjectTemplate = result?.subject_template ?? undefined;
          }
        } else {
          const templateId = Number(template.id);
          if (Number.isNaN(templateId)) {
            return;
          }
          const result = await fetchTemplateText({
            id: templateId,
            query: "{text,subject_template}"
          }).unwrap();
          text =
            result?.text ??
            (result as { data?: { text?: string } })?.data?.text ??
            "";
          subjectTemplate =
            result?.subject_template ??
            (result as { data?: { subject_template?: string } })?.data
              ?.subject_template;

          if (isLeads) {
            text = mapPatientMergeTagsForLeads(text);
            subjectTemplate = subjectTemplate
              ? mapPatientMergeTagsForLeads(subjectTemplate)
              : subjectTemplate;
          }
        }
      } catch (err) {
        console.error("Failed to fetch template text:", err);
        dispatch(
          setCustomMessage(
            "Failed to load template. Please try again or check your connection."
          )
        );
        dispatch(setOpen(true));
        return;
      }

      try {
        editorInstance.selection.moveToBookmark(bookmark);
      } catch (e) {
        // bookmark restore failed, cursor may be at default position
      }

      const parsed = getParsedWordProcessorValue(text);
      const rawHtml = parsed.html;
      const html = getTextWithMergeTagsValues({
        text: rawHtml,
        getMergeTagValue: mergeTagsValuesEnabled
          ? getMergeTagValue
          : getMergeTagNameOrEmptyValue,
        replaceMergeTagName,
        insertMergeTagValues: mergeTagsValuesEnabled,
        allowHtml: allowHtmlFeature
      });

      if (html.trim()) {
        editorInstance.focus();
        editorInstance.insertContent(html);
        const contentToSync = editorInstance.getContent();
        flushSync(() => {
          handleChangeValue(contentToSync, editorInstance);
        });
      }
      const fallbackSubjectTemplate =
        isLeads && getTemplateSource(template) === "default"
          ? mapPatientMergeTagsForLeads(template.subject_template ?? "")
          : template.subject_template;
      const insertedPayload: TextTemplateInsertedPayload = {
        ...template,
        text,
        subject_template: getInsertedTemplateSubject(
          subjectTemplate ?? fallbackSubjectTemplate
        )
      };
      onTemplateInserted?.(insertedPayload as TextTemplate);
    },
    [
      dispatch,
      fetchTemplateText,
      fetchCommsTemplateText,
      isLeads,
      onTemplateInserted,
      mergeTagsValuesEnabled,
      getMergeTagValue,
      getMergeTagNameOrEmptyValue,
      replaceMergeTagName,
      allowHtmlFeature,
      handleChangeValue,
      getInsertedTemplateSubject
    ]
  );

  const onTemplateInsertRef = useRef(handleDropdownInsertTemplate);
  onTemplateInsertRef.current = handleDropdownInsertTemplate;
  const stableOnTemplateInsert = useCallback(
    (template: TextTemplateListItem, editor: Editor) => {
      return onTemplateInsertRef.current(template, editor);
    },
    []
  );

  const getSaleOptions = useCallback(() => {
    return saleOptionsRef.current;
  }, []);

  // Keyed on the settings' content, not their identity, so a caller rebuilding an equal settings
  // object on every render changes nothing. A real change is written into the running editor
  // (applyFormModeConfig): tinymce-react reads `init` only when the editor starts.
  const formSettingsKey = formSettings ? JSON.stringify(formSettings) : "";
  // The Form layout tools (the table "Lines" control and signature boxes) come with Form mode only.
  const formLayoutEnabled = Boolean(formSettingsKey);
  // Read through a ref: the toolbar's buttons are registered once, when the editor starts.
  const hasFormEditorToolbar = formLayoutEnabled && Boolean(formEditorToolbar);
  const formEditorToolbarRef = useLatestRef(formEditorToolbar);
  // The width between the Form's margins, in inches. Also read through a ref: the signature box
  // size control and the image upload are handed to the editor once, and must go by the margins
  // as they are now.
  const formContentWidthIn = useMemo(
    () =>
      formSettingsKey
        ? formContentWidthPx(JSON.parse(formSettingsKey) as FormSettings) /
          FORM_PAGE_DPI
        : undefined,
    [formSettingsKey]
  );
  const formContentWidthInRef = useLatestRef(formContentWidthIn);
  // SN-19949: blanks become fields only where a Form is being filled (the print dialog, with merge-tag values on),
  // never while one is being written. Read through a ref: setup runs once, and either can change after it.
  const blankFieldsEnabledRef = useLatestRef(
    Boolean(mergeTagsValuesEnabled && flags[HTML_FORMS_FEATURE])
  );
  // The invoice table tag is edited from a chip, and inserted from its dialog, while a template is written
  // (invoice-table-tag.utils). Only an editor that offers merge tags in HTML can ever do that, so only those get
  // the editor hooks at all. Read through a ref: setup runs once, and the flag can arrive after it.
  // readOnlyFeature, not the prop: featuresGroup="readOnly" makes an editor read-only too.
  const canEditInvoiceTableTags = Boolean(
    allowMergeTagsFeature && allowHtmlFeature
  );
  const invoiceTableTagEditRef = useLatestRef(
    flags[INVOICE_TABLE_TAG_EDIT_FEATURE] === true &&
      canEditInvoiceTableTags &&
      !mergeTagsValuesEnabled &&
      !readOnlyFeature
  );
  const [invoiceTableTagEdit, setInvoiceTableTagEdit] = useState<{
    request: InvoiceTableTagRequest;
    target?: InvoiceTableTagTarget;
  } | null>(null);
  const openInvoiceTableTagDialog = useCallback(
    (
      editorId: string,
      request: InvoiceTableTagRequest,
      target?: InvoiceTableTagTarget
    ) => {
      setInvoiceTableTagEdit({ request, target });
      dispatch(
        setDataParams({
          id: editorId,
          openSelectSaleConfigurationDialog: true
        })
      );
    },
    [dispatch]
  );
  // The tag's text after an Update, so the caret can find the edited table again once the dialog closes: by then
  // its text is no longer what it was when it was clicked.
  const updatedInvoiceTableTagRef = useRef<string | undefined>(undefined);
  const handleInvoiceTableTagApply = useCallback(
    (options: Record<string, any>) => {
      const editor = editorRef.current;
      if (!editor || !invoiceTableTagEdit) return;
      const { request, target } = invoiceTableTagEdit;
      if (request.mode === "insert" || !target) {
        editor.focus();
        editor.insertContent(invoiceTableTag(options));
        return;
      }
      const next = withUnknownOptions(options, request.options);
      // Unchanged: the tag keeps its exact text, whatever order its options were written in.
      if (sameTagOptions(next, request.options)) return;
      const updatedTag = invoiceTableTag(next);
      if (replaceInvoiceTableTag(editor, target, updatedTag)) {
        updatedInvoiceTableTagRef.current = updatedTag;
      } else {
        dispatch(
          setCustomMessage(
            "The invoice table could not be updated because it is no longer in the template. Insert it again from Merge Tags."
          )
        );
        dispatch(setOpen(true));
      }
    },
    [dispatch, invoiceTableTagEdit]
  );
  const handleInvoiceTableTagRemove = useCallback(() => {
    const editor = editorRef.current;
    if (editor && invoiceTableTagEdit?.target) {
      removeInvoiceTableTag(editor, invoiceTableTagEdit.target);
    }
  }, [invoiceTableTagEdit]);
  const handleInvoiceTableTagDone = useCallback(() => {
    const editor = editorRef.current;
    const target = invoiceTableTagEdit?.target;
    const wasTagRequest = Boolean(invoiceTableTagEdit);
    const updatedTag = updatedInvoiceTableTagRef.current;
    updatedInvoiceTableTagRef.current = undefined;
    setInvoiceTableTagEdit(null);
    if (!editor || !wasTagRequest) return;
    // The dialog hands focus back to the page, not the editor, so the next keystroke went nowhere. Once its close
    // has committed, focus the editor and leave the caret just after the table: left selected, the next Enter
    // would reopen the dialog and the next keystroke would type over the table.
    window.setTimeout(() => {
      if (editor.removed) return;
      editor.focus();
      if (target) placeCaretAfterInvoiceTableTag(editor, target, updatedTag);
    }, 0);
  }, [invoiceTableTagEdit]);

  const signatureBoxes = useMemo(
    () =>
      formLayoutEnabled
        ? { getMaxWidthIn: () => formContentWidthInRef.current }
        : undefined,
    [formLayoutEnabled, formContentWidthInRef]
  );

  // In Form mode, images are stored downscaled to print quality with their printed size in inches.
  const formImageUpload = useMemo(
    () =>
      signatureBoxes
        ? createFormImageUpload(
            () => editorRef.current,
            signatureBoxes.getMaxWidthIn
          )
        : null,
    [signatureBoxes]
  );

  const setupHandler = useCallback(
    (editor: Editor) => {
      if (formLayoutEnabled) {
        addTableLinesButton(editor);
        chipTagsWithOptions(editor);
        addCheckboxToggle(editor);
        addBlankFields(editor, {
          isEnabled: () => blankFieldsEnabledRef.current
        });
      } else if (canEditInvoiceTableTags) {
        chipTagsWithOptions(editor, {
          isEnabled: () => invoiceTableTagEditRef.current,
          shouldWrap: isInvoiceTableTag
        });
      }
      if (canEditInvoiceTableTags) {
        attachInvoiceTableTagEditing(editor, {
          isEnabled: () => invoiceTableTagEditRef.current,
          // A Form's chips are named by the Digital Form editor.
          showLabels: !formLayoutEnabled,
          onEdit: (target) =>
            openInvoiceTableTagDialog(
              editor.id,
              { mode: "edit", options: invoiceTableTagOptions(target.tag) },
              target
            )
        });
      }
      if (hasFormEditorToolbar) {
        addFormEditorButtons(editor, () => formEditorToolbarRef.current);
      }
      // TinyMCE scans and uploads every data: image on each SetContent. Its uploader exists only once
      // the content body loads, after setup; PreInit fires once, then, before the first content lands.
      if (formImageUpload) {
        const { shouldUpload } = formImageUpload;
        editor.once("PreInit", () =>
          editor.editorUpload.addFilter(shouldUpload)
        );
      }
      setupWordProcessor({
        editor,
        showTemplatesDialog: handleShowTemplatesDialog,
        showSignDocumentDialog: handleShowSignDocumentDialog,
        allowContactFormPlaceholder: allowContactFormPlaceholderFeature,
        onInsertContactFormPlaceholder: handleInsertContactFormPlaceholder,
        onInsertMergeTag: mergeTagsValuesEnabled
          ? getMergeTagValue
          : getMergeTagNameOrEmptyValue,
        setIsRecording,
        allowHtml: allowHtmlFeature,
        allowAlign: allowAlignmentsFeature,
        allowAIPromptInterface:
          isAIPromptInterfaceEnabled && allowAIPromptInterfaceFeature,
        preventLineBreak,
        onClickMergeTagSignButton: handleOnClickMergeTagSignButton,
        onClickSelectInvoiceButton: handleOnClickSelectInvoiceButton,
        fieldInnerStatesRef,
        textTemplates,
        onTemplateInsert: stableOnTemplateInsert,
        onPrefetchTemplateText: stablePrefetchTemplateText,
        searchTemplates,
        allowImages: allowImagesFeature,
        getSaleOptions,
        mergeTagsValuesEnabled: Boolean(mergeTagsValuesEnabled),
        signatureBoxes,
        keepPrintedImageSizes: Boolean(formSettingsKey),
        openInvoiceTableDialog: () => {
          if (!invoiceTableTagEditRef.current) return false;
          // Opens with the table last applied in this editor, or a new table's defaults.
          openInvoiceTableTagDialog(editor.id, {
            mode: "insert",
            options: saleOptionsRef.current ?? {}
          });
          return true;
        }
      });
      onEditorSetup?.(editor);
    },
    [
      formImageUpload,
      handleShowTemplatesDialog,
      handleShowSignDocumentDialog,
      allowContactFormPlaceholderFeature,
      handleInsertContactFormPlaceholder,
      mergeTagsValuesEnabled,
      getMergeTagValue,
      getMergeTagNameOrEmptyValue,
      allowHtmlFeature,
      isAIPromptInterfaceEnabled,
      allowAIPromptInterfaceFeature,
      preventLineBreak,
      handleOnClickMergeTagSignButton,
      handleOnClickSelectInvoiceButton,
      allowAlignmentsFeature,
      textTemplates,
      stableOnTemplateInsert,
      stablePrefetchTemplateText,
      searchTemplates,
      allowImagesFeature,
      getSaleOptions,
      onEditorSetup,
      formLayoutEnabled,
      signatureBoxes,
      formSettingsKey,
      hasFormEditorToolbar,
      formEditorToolbarRef,
      blankFieldsEnabledRef,
      invoiceTableTagEditRef,
      openInvoiceTableTagDialog,
      canEditInvoiceTableTags
    ]
  );

  const formModeConfig = useMemo(
    () =>
      formSettingsKey
        ? buildFormModeEditorConfig(JSON.parse(formSettingsKey) as FormSettings)
        : null,
    [formSettingsKey]
  );

  const init = useMemo(
    () =>
      ({
        base_url: "/tinymce",
        suffix: ".min",
        theme_url: "/tinymce/themes/silver/theme.min.js",
        model_url: "/tinymce/models/dom/model.min.js",
        min_height: height,
        statusbar: false,
        height: height,
        paste_as_text: pasteAsTextByDefault || !allowHtmlFeature,
        readonly: readOnlyFeature ? true : undefined,
        placeholder,
        branding: false,
        color_default_background: "#FFFF00",
        remove_trailing_brs: false,
        custom_undo_redo_levels: allowUndoRedoFeature ? 20 : 0,
        image_title: false,
        fix_list_elements: true,
        forced_root_block: "div",
        // SN-19110: a link entered without a scheme ("google.com") must resolve to the
        // external site, not a path relative to the current page. link_default_protocol
        // prepends https:// in the link dialog; convert_urls:false stops TinyMCE from
        // relativizing URLs (its default convert_urls/relative_urls glued "google.com"
        // onto …/documents/new). Together: "google.com" -> "https://google.com", stored absolute.
        link_default_protocol: "https",
        convert_urls: false,
        mergetags_prefix: "{{",
        mergetags_suffix: "}}",
        mergetags_list: allowMergeTagsFeature ? mergeTagsOptions : undefined,
        extended_valid_elements: allowContactFormPlaceholderFeature
          ? "button[class|data-id|style],span[*],div[*]"
          : "button[class|data-id|style],span[*]",
        valid_children: "+body[button]",
        content_style: `
        img.mce-pagebreak {
          cursor: pointer;
        }

        .sign-button, .select-invoice-button {
              background-color: #054743;
              border: none;
              color: white;
              padding: 8px 16px;
              text-align: center;
              text-decoration: none;
              display: inline-block;
              font-size: 14px;
              margin: 4px 2px;
              cursor: pointer;
              border-radius: 4px;
            }

        .replace-wildcard {
              background-color: #fdecea;
              color: #b3261e;
              border: 1px solid #f5c2c0;
              border-radius: 3px;
              padding: 0 4px;
              font-weight: 600;
              cursor: pointer;
              white-space: nowrap;
            }
          ${
            allowContactFormPlaceholderFeature
              ? `[data-id="${CONTACT_FORM_PLACEHOLDER_ID}"] {
              width: 870px;
              max-width: 100%;
              height: 700px;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 6px;
              padding: 24px;
              text-align: center;
              line-height: 1.7;
              color: #667085;
              font-size: 14px;
              letter-spacing: 0.3px;
              background: repeating-linear-gradient(-45deg, #f2f4f7, #f2f4f7 12px, #eaecf0 12px, #eaecf0 24px);
              border: 1px dashed #d0d5dd;
              border-radius: 8px;
              user-select: none;
              margin: 8px 0;
            }`
              : ""
          }
          ${
            // Without it the string is byte-identical to before (no extra blank line).
            extraContentStyle ? `${extraContentStyle}\n          ` : ""
          }${formModeConfig?.content_style ?? ""}`,
        paste_preprocess: (_editor: Editor, args: any) => {
          const content = String(args.content ?? "");

          if (
            !allowImagesFeature &&
            content.startsWith("<img") &&
            content.includes(" src")
          ) {
            args.content = "";
          }
          if (!allowHtmlFeature) {
            args.content = getHtmlLikeText(args.content);
          } else {
            args.content = sanitizeHTML(
              args.content ?? "",
              formModeConfig
                ? { profile: formModeConfig.sanitizeProfile }
                : undefined
            );
          }
        },
        plugins: getPlugins({
          readOnly: readOnlyFeature,
          allowUndoRedo: allowUndoRedoFeature,
          allowHtml: allowHtmlFeature,
          allowImages: allowImagesFeature,
          allowTables: allowTablesFeature,
          allowLists: allowListsFeature,
          allowCode: allowCodeFeature,
          allowMergeTags: allowMergeTagsFeature,
          allowPreview: allowPreviewFeature,
          allowFullscreen: allowFullscreenFeature
        }),
        pagebreak_split_block: true,
        quickbars_selection_toolbar: false,
        quickbars_insert_toolbar: false,
        table_toolbar: withTableLinesButton(
          allowHtmlFeature && allowTablesFeature
            ? "tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tabledelete | tablecopyrow tablecutrow tablepasterowafter tablepasterowbefore | tablecellvalign tablecellborderwidth tablecellborderstyle tablecellbordercolor tablecellbackgroundcolor"
            : "",
          formLayoutEnabled
        ),
        ...(hasFormEditorToolbar && { toolbar_mode: "wrap" }),
        toolbar: hasFormEditorToolbar
          ? FORM_EDITOR_TOOLBAR
          : appendToolbarItems(
              getToolbarFeatures({
                readOnly: readOnlyFeature,
                allowUndoRedo: allowUndoRedoFeature,
                allowHtml: allowHtmlFeature,
                allowImages: allowImagesFeature,
                allowTables: allowTablesFeature,
                allowTemplates: allowTemplatesFeature,
                allowSignDocument: allowSignDocumentFeature,
                allowLists: allowListsFeature,
                allowAlignments: allowAlignmentsFeature,
                allowCode: allowCodeFeature,
                allowMergeTags: allowMergeTagsFeature,
                allowRemoveFormat: allowRemoveFormatFeature,
                allowPasteAsText: allowPasteAsTextFeature,
                allowPreview: allowPreviewFeature,
                allowFullscreen: allowFullscreenFeature,
                allowSpeechToText: allowSpeechToTextFeature,
                allowContactFormPlaceholder: allowContactFormPlaceholderFeature
              }),
              extraToolbarItems
            ),
        font_size_formats: allowHtmlFeature
          ? "8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt 60pt 72pt 96pt"
          : "",
        font_family_formats:
          formModeConfig?.font_family_formats ?? "LatoRegular=LatoRegular",
        ...(formModeConfig && {
          table_default_attributes: formModeConfig.table_default_attributes
        }),
        icons: "material",
        icons_url: "/tinymce/icons/material/icons.js",
        paste_merge_formats: false,
        smart_paste: false,
        paste_webkit_styles: "all",
        resize: false,
        menubar: false,
        browser_spellcheck: true,
        contextmenu: false,
        automatic_uploads: Boolean(allowImagesFeature),
        paste_data_images: Boolean(allowImagesFeature),
        file_picker_types: "image",
        file_picker_callback: allowImagesFeature ? fileCallback : undefined,
        images_upload_handler: formImageUpload
          ? formImageUpload.uploadHandler
          : allowImagesFeature && maxImageSizeMB
            ? imagesUploadCallback
            : undefined,
        image_dimensions: false,
        setup: setupHandler
      }) as any,
    [
      height,
      pasteAsTextByDefault,
      allowHtmlFeature,
      readOnlyFeature,
      placeholder,
      allowUndoRedoFeature,
      allowMergeTagsFeature,
      mergeTagsOptions,
      allowImagesFeature,
      allowTablesFeature,
      allowListsFeature,
      allowCodeFeature,
      allowPreviewFeature,
      allowFullscreenFeature,
      allowTemplatesFeature,
      allowSignDocumentFeature,
      allowAlignmentsFeature,
      allowRemoveFormatFeature,
      allowPasteAsTextFeature,
      allowSpeechToTextFeature,
      allowContactFormPlaceholderFeature,
      fileCallback,
      imagesUploadCallback,
      setupHandler,
      maxImageSizeMB,
      extraContentStyle,
      extraToolbarItems,
      formModeConfig,
      formLayoutEnabled,
      formImageUpload,
      hasFormEditorToolbar
    ]
  );

  const onBlurHandler = useCallback(() => {
    if (!isRecording) {
      allowToolbarAutohideFeature && hideToolbar(id);
    }
    field.onBlur();
    onBlur?.();
  }, [allowToolbarAutohideFeature, field, id, isRecording, onBlur]);

  const onFocusInHandler = useCallback(() => {
    if (!readOnlyFeature) {
      allowToolbarAutohideFeature && showToolbar(id);
      setDoNotFetchMergeTagsDataSources(false);
    }

    if (defaultFontSize) {
      editorRef.current?.execCommand(
        "FontSize",
        false,
        `${Number(defaultFontSize) || 16}px`
      );
    }

    eventBus.emit(
      LAST_FOCUSED_WORD_PROCESSOR_FIELD,
      editorRef.current as Editor
    );

    onFocus?.();
  }, [
    defaultFontSize,
    readOnlyFeature,
    onFocus,
    allowToolbarAutohideFeature,
    id,
    setDoNotFetchMergeTagsDataSources
  ]);

  const onKeyDownHandler = useCallback(
    (event: any) => {
      onKeyDown?.(event);
    },
    [onKeyDown]
  );

  // ENG-2277: tinymce-react reads `init` once, when the editor starts, so a Form's paper, margins and
  // table lines changed afterwards never reached the page. They are written into the running editor
  // instead: its page style is replaced, which keeps the content, the cursor and the undo history.
  const formModeConfigRef = useLatestRef(formModeConfig);
  const applyFormModeConfig = useCallback(
    (editor: Editor) => {
      const doc = editor.getDoc();
      if (!doc) return;
      const config = formModeConfigRef.current;
      doc.getElementById(FORM_MODE_LIVE_STYLE_ID)?.remove();
      if (!config) return;
      const style = doc.createElement("style");
      style.id = FORM_MODE_LIVE_STYLE_ID;
      style.textContent = config.content_style;
      doc.head.appendChild(style);
      editor.options.set(
        "table_default_attributes",
        config.table_default_attributes
      );
    },
    [formModeConfigRef]
  );
  useEffect(() => {
    const editor = editorRef.current;
    if (editor?.initialized) applyFormModeConfig(editor);
  }, [applyFormModeConfig, formModeConfig]);

  const onInitHandler = useCallback(
    (_event: any, editor: Editor) => {
      editorRef.current = editor;
      onEditorInit?.(editor);
      if (formModeConfigRef.current) applyFormModeConfig(editor);

      if (!allowHtmlFeature) {
        removeDefaultShortcuts(editor);
      }

      // Check if any plugins failed to load by comparing configured vs loaded
      // Use the editor instance passed to onInit, not tinymce.activeEditor
      let pluginsLoadedSuccessfully = true;

      if (editor?.plugins) {
        const loadedPlugins = Object.keys(editor.plugins);

        const configuredPlugins = (init.plugins || []) as string[];

        // Check if all configured plugins were loaded
        const missingPlugins = configuredPlugins.filter(
          (plugin) => !loadedPlugins.includes(plugin)
        );

        if (missingPlugins.length > 0) {
          console.error("Missing plugins detected:", missingPlugins);
          pluginsLoadedSuccessfully = false;
          setHasLoadError(true);
        }
      }

      // Only mark as initialized if plugins loaded successfully
      if (pluginsLoadedSuccessfully) {
        setInitialized(new Date().getTime());

        // Allow a brief moment for TinyMCE to finish initial formatting
        // before enabling change detection
        setTimeout(() => {
          setIsInitialContentLoading(false);
        }, 100);
      } else {
        setHasLoadError(true);
      }

      if (autoFocus) {
        editor.focus();
      } else {
        allowToolbarAutohideFeature && !isContextSaleLoading && hideToolbar(id);
      }
      afterInitCallback?.(editor);
    },

    [
      afterInitCallback,
      allowHtmlFeature,
      allowToolbarAutohideFeature,
      applyFormModeConfig,
      formModeConfigRef,
      autoFocus,
      isContextSaleLoading,
      id,
      init.plugins,
      onEditorInit
    ]
  );

  const isReady = useMemo(
    () => [!isBusy, initialized].every(Boolean),
    [isBusy, initialized]
  );

  useEffect(() => {
    const editor = editorRef.current;
    if (!isReady || !editor || height == null) return;

    const container = editor.getContainer();
    if (container) {
      container.style.height = `${height}px`;
    }
  }, [height, isReady]);

  // Monitor for TinyMCE loading errors
  useEffect(() => {
    // Check if TinyMCE failed to load globally
    if ((window as any).tinymceLoadError) {
      setHasLoadError(true);
      return;
    }

    // Intercept console errors to detect TinyMCE loading failures
    const originalError = console.error;
    console.error = (...args) => {
      const errorMessage = args.join(" ");
      if (
        errorMessage.includes("Failed to load plugin:") ||
        errorMessage.includes("Failed to load theme:") ||
        errorMessage.includes("Failed to load model:") ||
        errorMessage.includes("Failed to load icons:")
      ) {
        originalError("TinyMCE error detected:", errorMessage);
        setHasLoadError(true);
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const handleFocusIn = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    if (target.closest(".tox-textfield")) {
      event.stopImmediatePropagation();
    }
  }, []);

  /*
  Workaround for Tiny Cloud that fixes URL field availability
  inside it's inner "Insert/Edit Link" modal
  https://github.com/tinymce/tinymce/issues/782
  */
  useEffect(() => {
    if (isReady) {
      document.addEventListener("focusin", handleFocusIn, true);

      return () => {
        document.removeEventListener("focusin", handleFocusIn, true);
      };
    }
  }, [isReady, handleFocusIn]);

  const controlSx = useMemo(() => {
    const shouldShow = !hidden && isReady && !hasLoadError;
    const opacity = hidden ? 0 : shouldShow ? 1 : 0;
    const heightValue = hidden ? "1px" : shouldShow ? undefined : "1px";

    return {
      ...sx,
      opacity,
      height: heightValue,
      ...(height != null && shouldShow
        ? {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0
          }
        : {}),
      // When the editor is not visible (opacity: 0, height: 1px), the TinyMCE
      // iframe still overflows its 1px container (due to min_height config) and
      // intercepts pointer events because CSS overflow defaults to "visible".
      // Inside the iframe, cursor is controlled by a separate document context,
      // causing the mouse cursor to disappear in some Chrome/Edge versions.
      // pointer-events: none prevents the invisible iframe from capturing events.
      ...(!shouldShow ? { pointerEvents: "none" } : {})
    } as SxProps;
  }, [hidden, isReady, hasLoadError, sx, height]);

  const hasError = !isValid && errorMessage;

  let showMergeTagsLoadingBar =
    mergeTagsValuesEnabled && !isMergeTagsDataSourceReady;

  if (
    allowToolbarAutohideFeature &&
    /*
    Check if editor instance's toolbar is not visible (see hideToolbar showToolbar functions)
    */
    (document.querySelector(`#${id} + div .tox-editor-header`) as HTMLElement)
      ?.style?.visibility !== "visible"
  ) {
    showMergeTagsLoadingBar = false;
  }

  useEffect(() => {
    if (autoFetchMergeTagsValues) {
      setDoNotFetchMergeTagsDataSources(false);
    }
  }, [autoFetchMergeTagsValues, setDoNotFetchMergeTagsDataSources]);

  useEffect(() => {
    if (
      isReady &&
      mergeTagsValuesEnabled &&
      isMergeTagsDataSourceReady &&
      editorRef.current
    ) {
      afterMergeTagsDataLoadedCallback?.(handleSubmitTemplatesDialog);
    }
  }, [
    afterMergeTagsDataLoadedCallback,
    isMergeTagsDataSourceReady,
    mergeTagsValuesEnabled,
    handleSubmitTemplatesDialog,
    isReady
  ]);

  useEffect(() => {
    onMergeTagsContextReady?.({
      getMergeTagValue,
      isReady: isMergeTagsDataSourceReady
    });
  }, [onMergeTagsContextReady, getMergeTagValue, isMergeTagsDataSourceReady]);

  const displayAiPrompt =
    allowAIPromptInterfaceFeature && isAIPromptInterfaceEnabled;

  return (
    <>
      {(!isReady || isLoading) && !hidden && !hasLoadError && (
        <WordProcessorLoadingSkeleton
          height={height}
          value={isLoading && loadingText ? loadingText : value || placeholder}
          sx={sx}
        />
      )}
      {hasLoadError && !hidden && (
        <WordProcessorErrorState height={height} sx={sx} />
      )}
      <>
        <FormControl
          required={required}
          fullWidth
          error={Boolean(hasError)}
          sx={controlSx}
        >
          {Boolean(label) && (
            <InputLabel
              disabled={readOnlyFeature}
              htmlFor={kebabCase(name)}
              title={label}
            >
              {label}
            </InputLabel>
          )}
          <Box
            position="relative"
            mb={displayAiPrompt ? "-34px" : undefined}
            sx={
              height != null
                ? {
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column"
                  }
                : undefined
            }
          >
            {!hasLoadError && (
              <EditorField
                key={id}
                id={id}
                disabled={readOnlyFeature}
                rollback={false}
                textareaName={field.name}
                apiKey={apiKey}
                value={value}
                onInit={onInitHandler as any}
                onEditorChange={onEditorChangeHandler as any}
                onFocusIn={onFocusInHandler}
                onKeyDown={onKeyDownHandler}
                onBlur={onBlurHandler}
                init={init}
                onClick={onClick}
              />
            )}

            {!readOnlyFeature && displayAiPrompt && (
              <AiPromptInlineField
                id={id}
                editorRef={editorRef}
                sx={aiPromptInlineFieldSx}
                onAskAi={onAskAi}
                handleChangeValue={handleChangeValue}
                placeholder={askAiInputPlaceholder}
                promptType={askAiPromptType}
              />
            )}
          </Box>
          {!hideLengthLimitation && smsSegmentCounter && !allowHtmlFeature ? (
            <SmsSegmentCounter
              text={smsCounterText}
              estimateMergeTags={estimateSmsMergeTags}
            />
          ) : (
            !hideLengthLimitation &&
            Boolean(maxLength || allowShowLengthFeature) && (
              <Typography sx={{ color: grey[700], pl: 0.25, fontSize: 12 }}>
                {length}
                {maxLength ? ` / ${maxLength}` : " characters"}
              </Typography>
            )
          )}
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
          {hasError && (
            <FormHelperText error={!!errorMessage}>
              {errorMessage}
            </FormHelperText>
          )}
        </FormControl>
        <TextTemplatesDialogV2
          fieldIdRef={fieldIdRef}
          allowInsertMergeTagsValuesFeature={allowInsertMergeTagsValuesFeature}
          templateTypes={templateTypes}
          show={showTemplatesDialog}
          onClose={handleCloseTemplatesDialog}
          onSubmit={handleSubmitTemplatesDialog(onTemplateInserted)}
          dialogZIndex={nestedDialogZIndex}
          templateSource={templateSource}
        />

        <SignDocumentDialog
          show={showSignDialog}
          onClose={handleCloseSignDocumentDialog}
          onSubmit={handleSubmitSignature}
          enableHandSignatureOnly={enableHandSignatureOnly}
        />
        <WordProcessorSelectSaleDialog
          id={id}
          nestedDialogZIndex={nestedDialogZIndex}
        />
        <WordProcessorSelectSaleConfigurationDialog
          id={id}
          tagRequest={invoiceTableTagEdit?.request}
          onTagApply={handleInvoiceTableTagApply}
          onTagRemove={handleInvoiceTableTagRemove}
          onTagDone={handleInvoiceTableTagDone}
        />
        <WordProcessorSilenceWarningDialog
          id={id}
          onContinue={handleSilenceWarningContinue}
          onClose={handleSilenceWarningClose}
          onPause={handleSilenceWarningPause}
          dialogZIndex={nestedDialogZIndex}
        />
      </>
      <MergeTagsLoadingBar show={showMergeTagsLoadingBar} />
    </>
  );
}
